import React from 'react'
import { ArrowDownOutlined, ArrowUpOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons'
import { Button, Card, Popconfirm, Select, Space } from 'antd'
import {
  PortalArticleTopicBlock,
  PortalArticleTopicItem,
} from '../../../../models/PortalArtigoModel'
import { ImageBlockEditor } from './ImageBlockEditor'
import { QuickSummaryEditor } from './QuickSummaryEditor'
import { SectionEditor } from './SectionEditor'
import { PortalSectionSaveControlProps } from '../../helpers/editor-section-save'
import { PortalSectionSaveButton } from '../PortalEditorTheme'

interface Props {
  item: PortalArticleTopicItem
  itemIndex: number
  itemsLength: number
  topic: PortalArticleTopicBlock
  topics: PortalArticleTopicBlock[]
  canMoveUp: boolean
  canMoveDown: boolean
  onChange: (item: PortalArticleTopicItem) => void
  onMove: (direction: -1 | 1) => void
  onDuplicate?: () => void
  onRemove?: () => void
  onMoveToTopic?: (targetTopicId: string) => void
  saveControl: PortalSectionSaveControlProps
}

export function PortalTopicItemEditor({
  item,
  itemIndex,
  itemsLength,
  topic,
  topics,
  canMoveUp,
  canMoveDown,
  onChange,
  onMove,
  onDuplicate,
  onRemove,
  onMoveToTopic,
  saveControl,
}: Props) {
  const label =
    item.type === 'image'
      ? 'Imagem'
      : item.type === 'quickSummary'
      ? 'Resumo rápido'
      : item.role === 'main'
      ? 'Tópico (H2)'
      : 'Subtópico (H3)'

  return (
    <Card
      id={`portal-topic-${topic.id}-item-${item.id || itemIndex}`}
      size="small"
      title={`${itemIndex + 1}. ${label}`}
      extra={
        <Space wrap>
          <Button size="small" icon={<ArrowUpOutlined />} disabled={!canMoveUp} onClick={() => onMove(-1)} />
          <Button size="small" icon={<ArrowDownOutlined />} disabled={!canMoveDown} onClick={() => onMove(1)} />
          {onDuplicate && <Button size="small" icon={<CopyOutlined />} onClick={onDuplicate}>Duplicar</Button>}
          {onRemove && (
            <Popconfirm
              title={`Remover ${label.toLowerCase()}`}
              description="Tem certeza? Esta ação remove o conteúdo deste item."
              okText="Remover"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={onRemove}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {item.type === 'image' && (
          <ImageBlockEditor
            value={item.image}
            anchorId={item.anchorId}
            onChange={(image, anchorId) => onChange({ ...item, image, anchorId })}
          />
        )}
        {item.type === 'quickSummary' && (
          <QuickSummaryEditor
            value={item.quickSummary}
            onChange={(quickSummary) => onChange({ ...item, quickSummary })}
          />
        )}
        {item.type === 'section' && (
          <SectionEditor
            value={item.section}
            role={item.role}
            onChange={(section) => onChange({ ...item, section })}
          />
        )}
        {item.type === 'section' && item.role === 'subtopic' && onMoveToTopic && topics.length > 1 && (
          <Select
            placeholder="Mover subtópico para outro tópico"
            style={{ width: 320, maxWidth: '100%' }}
            value={undefined}
            onChange={onMoveToTopic}
            options={topics
              .filter((candidate) => candidate.id !== topic.id)
              .map((candidate, index) => ({
                value: candidate.id,
                label: `Mover para tópico ${index + 1}`,
              }))}
          />
        )}
        <PortalSectionSaveButton {...saveControl} />
      </Space>
    </Card>
  )
}
